import { enrichScoreCompletedLayout } from './applyPostScoreServerResult';
import { mergeCourtTimerFromPrevious } from '../utils/matchTimerUtils';

/**
 * Apply a SCORE_COMPLETED command result payload to local session state.
 *
 * Used by both:
 *  - The initiator tab: immediately after receiving the EF response (replaces
 *    applyOptimisticPostScoreLayout with authoritative DB values).
 *  - Passive host tabs: when a session_host_actions realtime INSERT arrives
 *    with action_type='SCORE_COMPLETED'.
 *
 * Idempotency: skips if result.layoutRevision is at or below the locally
 * tracked revision, preventing double-application or stale rewrites.
 *
 * @param {object} result
 *   The SCORE_COMPLETED result payload from the EF response or realtime row:
 *   { layoutRevision, courtAssignments, nextAssignments, waitingList,
 *     playerWaitMetrics, courtNumber, promoted, promotedFromSlot, fills }
 *
 * @param {object} setters
 *   React state setters; waitingList and playerWaitMetrics are optional.
 *   { setCourtAssignments, setNextAssignments, setWaitingList?, setPlayerWaitMetrics? }
 *
 * @param {React.MutableRefObject<number>} localLayoutRevisionRef
 *   Tracks the highest layoutRevision applied to this tab's local state.
 *   Pass null to skip the idempotency gate (e.g. first apply on passive tab).
 *
 * @param {function} [mergeWaitMetrics]
 *   Optional merge helper: (prev, incoming) => merged.
 *   When provided, playerWaitMetrics is merged rather than replaced.
 *
 * @param {function} [getPreviousLayout]
 *   Optional snapshot of local layout before apply, used to carry client-only
 *   matchmaking metadata (relaxed badge) onto promoted courts in the same frame.
 *   Returns { courtAssignments?, nextAssignments? }.
 */
export function mergeScoreCompletedCourtAssignments(courtAssignments, previousCourts, result) {
  if (!Array.isArray(courtAssignments)) return courtAssignments;
  const previous = Array.isArray(previousCourts) ? previousCourts : [];
  const scoredCourtNumber =
    result?.courtNumber != null ? Number(result.courtNumber) : null;
  return courtAssignments.map((court) => {
    const prev = previous.find(
      (c) => Number(c.courtNumber) === Number(court.courtNumber),
    );
    const isScoredCourt =
      scoredCourtNumber != null && Number(court.courtNumber) === scoredCourtNumber;
    return mergeCourtTimerFromPrevious(prev, court, {
      restartTimer: isScoredCourt && result?.promoted === true,
    });
  });
}

export function applyScoreCompletedResult(
  result,
  setters,
  localLayoutRevisionRef,
  mergeWaitMetrics,
  getPreviousLayout,
) {
  if (!result || typeof result.layoutRevision !== 'number') return;

  const {
    setCourtAssignments,
    setNextAssignments,
    setWaitingList,
    setPlayerWaitMetrics,
  } = setters;

  // Idempotency: skip if we already applied this revision or a newer one
  if (
    localLayoutRevisionRef != null &&
    typeof localLayoutRevisionRef.current === 'number' &&
    result.layoutRevision <= localLayoutRevisionRef.current
  ) {
    return;
  }

  const previous = typeof getPreviousLayout === 'function' ? getPreviousLayout() : {};
  const enriched = enrichScoreCompletedLayout(
    result,
    previous.courtAssignments || [],
    previous.nextAssignments || [],
  );

  if (enriched.courtAssignments != null && setCourtAssignments) {
    const mergedCourts = mergeScoreCompletedCourtAssignments(
      enriched.courtAssignments,
      previous.courtAssignments || [],
      result,
    );
    setCourtAssignments(mergedCourts);
  }

  if (enriched.nextAssignments != null && setNextAssignments) {
    setNextAssignments(enriched.nextAssignments);
  }

  if (result.waitingList != null && setWaitingList) {
    setWaitingList(result.waitingList);
  }

  if (result.playerWaitMetrics != null && setPlayerWaitMetrics) {
    if (typeof mergeWaitMetrics === 'function') {
      setPlayerWaitMetrics((prev) => mergeWaitMetrics(prev, result.playerWaitMetrics));
    } else {
      setPlayerWaitMetrics(result.playerWaitMetrics);
    }
  }

  if (localLayoutRevisionRef != null) {
    localLayoutRevisionRef.current = result.layoutRevision;
  }
}
